// Frontend mirror of backend/src/utils/transportCostAllocation.js — keep the two in sync.
//
// BY_SQM / BY_CBM / BY_LDM per-line metrics are derived from the product
// dimensions (mm) + loaded quantity via the shared dimensionConversion helpers
// (the single home for the piece → m²/m³ geometry), per
// docs/decisions/2026-07-03-transport-cost-allocation-rules.md. The commercial
// unit is honoured exactly like the backend twin: when it already matches the
// metric (m² under BY_SQM, m³ under BY_CBM) the quantity IS the metric and no
// dimensions are needed; a mismatched unit derives through product dimensions
// and yields null (→ MISSING_DIMENSIONS) when they are absent. LDM has its own
// dedicated package-based footprint rule (see calculatePackagingLdm below).
import {
  calculateAreaM2,
  calculateVolumeM3,
  normalizeQuantityUnit,
  type ConversionSource,
} from './dimensionConversion';
export { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from './polishNumberInput';

// Trailer floor width (metres) for the LDM (loading-metre) metric. Mirrors
// STANDARD_TRAILER_WIDTH_METERS in the backend twin. This is the SINGLE home for
// the LDM divisor on the frontend — sharedTransportFormUtils and
// calculatePackagingLdm below both read this constant so the LDM shown to the
// operator and the LDM used to split the cost agree by construction; there is
// no separate DEFAULT_TRAILER_WIDTH_METERS constant.
export const STANDARD_TRAILER_WIDTH_METERS = 2.4;

// F1 (trailer capacity indicator) — SINGLE HOME, byte-identical to the backend
// twin. A standard European curtain-sider/mega trailer: 13.6 m of loading
// length, 24 000 kg max payload. Used ONLY for a display-only utilization
// badge (capacity vs loaded LDM/weight) — never enforced as a save-blocking
// limit, since real trailers/permits vary.
export const TRAILER_CAPACITY_LDM = 13.6;
export const TRAILER_PAYLOAD_KG = 24000;

export const TRANSPORT_COST_ALLOCATION_METHODS = [
  'BY_COMMERCIAL_QTY',
  'BY_SQM',
  'BY_CBM',
  'BY_LDM',
  'MANUAL',
] as const;

export type AllocationMethod = typeof TRANSPORT_COST_ALLOCATION_METHODS[number];

// Ordered method list for the "Podział kosztu na pozycje" select. Kept next to
// the method list so the picker order (qty / m² / m³ / LDM / manual) has
// a single home shared by the form and the detail page.
export const TRANSPORT_COST_ALLOCATION_METHOD_ORDER: readonly AllocationMethod[] = [
  'BY_COMMERCIAL_QTY',
  'BY_SQM',
  'BY_CBM',
  'BY_LDM',
  'MANUAL',
];

const DEFAULT_ALLOCATION_METHOD: AllocationMethod = 'BY_COMMERCIAL_QTY';

// Persistence bound for the money fields backing this calculation
// (NUMERIC(14,2) in migration 091): 12 integer digits + 2 decimals.
export const MAX_MONEY_AMOUNT = 999999999999.99;

// Package-based LDM bounds (migration 093) — SINGLE HOME on the frontend,
// mirroring PACKAGING_BOUNDS in the backend twin. Used by the product form and
// the per-line packaging editor Form.Item rules; change values in BOTH mirrors.
export const PACKAGING_BOUNDS = {
  maxPiecesPerPackage: 100000,
  maxPackageDimensionMm: 20000,
  minStackLayers: 1,
  maxStackLayers: 10,
  // F2 (manual LDM override, migration 094): TransportCargoItem."manualLdm" is
  // NUMERIC(12,3) with a DB CHECK of >= 0 only; the upper bound is enforced
  // here (Form.Item rule) and mirrored on the backend service normalizer so an
  // absurd operator typo is a clean validation error, not a value that
  // silently distorts the allocation math. A real trailer LDM never
  // approaches this.
  maxManualLdm: 99999,
};

// ---------------------------------------------------------------------------
// Packaging field Form.Item rules — SINGLE HOME shared by ProductDetail.tsx and
// TransportOrdersCargoSection.tsx's per-line packaging editor, so the two never
// drift. Per .claude/rules/frontend-pages.md, numeric inputs editing a saved
// value must NOT rely on InputNumber min/max clamps (antd silently coerces an
// out-of-range entry on blur and the form then saves the coerced value). These
// are custom `validator` rules (not a bare `min`) because the backend
// (normalizeOptionalPositiveDecimal / normalizeOptionalIntegerInRange in
// productService.js and transportService.js) treats packaging fields as
// "empty clears the field, anything else must be STRICTLY POSITIVE (>0)" — a
// plain `{ min: 0 }` rule would let 0 through client-side and then 400 on save.
// ---------------------------------------------------------------------------
type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

function isEmptyFormValue(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

// piecesPerPackage / packageLength / packageWidth: optional, but when present
// must be a finite number > 0 and <= max. Mirrors
// normalizeOptionalPositiveDecimal's "undefined stays unset, null/'' clears,
// anything else must be strictly positive" contract.
export function packagingPositiveDecimalRule(t: TranslateFn, max: number) {
  return {
    validator: (_: unknown, value: unknown) => {
      if (isEmptyFormValue(value)) {
        return Promise.resolve();
      }
      const numeric = Number(value);
      if (!Number.isFinite(numeric) || numeric <= 0) {
        return Promise.reject(new Error(t('transport:packagingValuePositiveInvalid')));
      }
      if (numeric > max) {
        return Promise.reject(new Error(t('transport:commercialAllocationValueOutOfRange', { max })));
      }
      return Promise.resolve();
    },
  };
}

// packageStackLayers: optional, but when present must be an integer within
// [min, max] (backend: normalizeOptionalIntegerInRange, 1..maxStackLayers).
export function packagingStackLayersRule(t: TranslateFn, min: number, max: number) {
  return {
    validator: (_: unknown, value: unknown) => {
      if (isEmptyFormValue(value)) {
        return Promise.resolve();
      }
      const numeric = Number(value);
      if (!Number.isInteger(numeric) || numeric < min || numeric > max) {
        return Promise.reject(new Error(t('transport:packagingStackLayersInvalid', { min, max })));
      }
      return Promise.resolve();
    },
  };
}

// F2 manualLdm: optional, but when present must be a finite number within
// [0, max] — unlike the packaging fields above, 0 is a VALID stored value
// (it just means "no override", same as absent — see resolveItemLdm's
// truthy check), so this intentionally allows >= 0 rather than requiring
// strictly positive. Mirrors the backend's normalizeOptionalNonNegativeDecimal.
export function manualLdmRule(t: TranslateFn, max: number) {
  return {
    validator: (_: unknown, value: unknown) => {
      if (isEmptyFormValue(value)) {
        return Promise.resolve();
      }
      const numeric = Number(value);
      if (!Number.isFinite(numeric) || numeric < 0) {
        return Promise.reject(new Error(t('transport:manualLdmValueInvalid')));
      }
      if (numeric > max) {
        return Promise.reject(new Error(t('transport:commercialAllocationValueOutOfRange', { max })));
      }
      return Promise.resolve();
    },
  };
}

export type TransportCostAllocationWarningCode =
  | 'ZERO_TOTAL_QUANTITY'
  | 'MIXED_UNITS'
  | 'ZERO_TOTAL_METRIC'
  | 'MISSING_DIMENSIONS'
  | 'MISSING_PACKAGING'
  | 'PACKAGE_EXCEEDS_TRAILER_WIDTH'
  | 'MANUAL_SUM_MISMATCH'
  | 'NEGATIVE_MANUAL_ALLOCATION'
  | 'NON_POSITIVE_QUANTITY';

export interface TransportCostAllocationInputItem {
  id?: string;
  quantity?: number | string | null;
  unit?: string | null;
  goodsValueTotal?: number | string | null;
  manualAllocatedTransportCost?: number | string | null;
  // Optional product identity + dimensions (mm) used by BY_SQM / BY_CBM /
  // BY_LDM. Absent for the quantity/manual methods, which ignore them.
  // `productName` mirrors the backend twin's preferred name field.
  name?: string | null;
  productName?: string | null;
  width?: number | string | null;
  length?: number | string | null;
  height?: number | string | null;
  thickness?: number | string | null;
  // Packaging (logistics) snapshot used by BY_LDM's package-based footprint.
  // Absent for the quantity/manual/m²/m³ methods, which ignore them.
  piecesPerPackage?: number | string | null;
  packageLength?: number | string | null;
  packageWidth?: number | string | null;
  packageLengthMm?: number | string | null;
  packageWidthMm?: number | string | null;
  packageStackLayers?: number | string | null;
  // F2 (manual LDM override): a positive value wins over the packaging-based
  // calculation for this line (see resolveItemLdm in the backend twin).
  manualLdm?: number | string | null;
}

export interface TransportCostAllocationInput {
  transportCost?: number | string | null;
  method?: AllocationMethod | string | null;
  items?: TransportCostAllocationInputItem[];
}

export interface TransportCostAllocationWarningItemRef {
  itemId?: string;
  name?: string | null;
}

export interface TransportCostAllocationWarning {
  code: TransportCostAllocationWarningCode;
  itemId?: string;
  // For MISSING_DIMENSIONS: the offending lines (id + product name), emitted as
  // a single aggregated warning — mirrors the backend twin's warning shape.
  items?: TransportCostAllocationWarningItemRef[];
}

// Single home for mapping warning codes to transport.json i18n keys — used by
// the form and the detail page.
export function commercialAllocationWarningKey(code: TransportCostAllocationWarningCode): string {
  switch (code) {
    case 'ZERO_TOTAL_QUANTITY':
      return 'commercialAllocationWarningZeroTotalQuantity';
    case 'MIXED_UNITS':
      return 'commercialAllocationWarningMixedUnits';
    case 'ZERO_TOTAL_METRIC':
      return 'commercialAllocationWarningZeroTotalMetric';
    case 'MISSING_DIMENSIONS':
      return 'commercialAllocationWarningMissingDimensions';
    case 'MISSING_PACKAGING':
      return 'commercialAllocationWarningMissingPackaging';
    case 'PACKAGE_EXCEEDS_TRAILER_WIDTH':
      return 'commercialAllocationWarningPackageExceedsTrailerWidth';
    case 'MANUAL_SUM_MISMATCH':
      return 'commercialAllocationWarningManualSumMismatch';
    case 'NEGATIVE_MANUAL_ALLOCATION':
      return 'commercialAllocationWarningNegativeManualAllocation';
    case 'NON_POSITIVE_QUANTITY':
    default:
      return 'commercialAllocationWarningNonPositiveQuantity';
  }
}

// Single home for the label i18n key of each item-level allocation method.
export function commercialAllocationMethodLabelKey(method: AllocationMethod): string {
  switch (method) {
    case 'BY_SQM':
      return 'commercialAllocationBySqm';
    case 'BY_CBM':
      return 'commercialAllocationByCbm';
    case 'BY_LDM':
      return 'commercialAllocationByLdm';
    case 'MANUAL':
      return 'commercialAllocationManual';
    case 'BY_COMMERCIAL_QTY':
    default:
      return 'commercialAllocationByCommercialQty';
  }
}

// Per-option tooltip i18n key (unit definitions from the rulebook). Only the
// dimension-based methods carry a hint; the rest return null (no tooltip).
export function commercialAllocationMethodTooltipKey(method: AllocationMethod): string | null {
  switch (method) {
    case 'BY_SQM':
      return 'commercialAllocationBySqmTooltip';
    case 'BY_CBM':
      return 'commercialAllocationByCbmTooltip';
    case 'BY_LDM':
      return 'commercialAllocationByLdmTooltip';
    default:
      return null;
  }
}

export interface CostAllocationMethodOptionDef {
  value: AllocationMethod;
  labelKey: string;
  tooltipKey: string | null;
}

// Ordered option definitions (value + i18n keys) for the "Podział kosztu na
// pozycje" picker: quantity / m² / m³ / LDM / manual. `excludeManual`
// drops MANUAL (split mode can't carry a manual per-line override). The single
// home for the picker contents so the form and the detail page never diverge.
export function buildCostAllocationMethodOptionDefs(
  { excludeManual = false }: { excludeManual?: boolean } = {},
): CostAllocationMethodOptionDef[] {
  return TRANSPORT_COST_ALLOCATION_METHOD_ORDER
    .filter((method) => !excludeManual || method !== 'MANUAL')
    .map((method) => ({
      value: method,
      labelKey: commercialAllocationMethodLabelKey(method),
      tooltipKey: commercialAllocationMethodTooltipKey(method),
    }));
}

export interface TransportCostAllocationItemResult {
  id?: string;
  quantity: number;
  unit: string | null;
  goodsValueTotal: number;
  manualAllocatedTransportCost: number;
  allocatedTransportCost: number;
  goodsCostPerUnit: number | null;
  transportCostPerUnit: number | null;
  landedCostTotal: number;
  landedCostPerUnit: number | null;
  // OSCAR (PDF-contract wave): per-METRIC unit costs ("na m² 2,94", "na m³
  // 170,57" — the owner's PDF, examples P1/P2). null when the metric cannot
  // be computed for this line (no dimensions/packaging) — render "—".
  transportCostPerAreaSqm: number | null;
  transportCostPerVolumeM3: number | null;
  transportCostPerLdm: number | null;
}

export interface TransportCostAllocationSummary {
  totalGoodsValue: number;
  totalAllocatedTransportCost: number;
  totalLandedCost: number;
  allocationDifference: number;
}

export interface TransportCostAllocationResult {
  method: AllocationMethod;
  items: TransportCostAllocationItemResult[];
  summary: TransportCostAllocationSummary;
  warnings: TransportCostAllocationWarning[];
}

interface NormalizedAllocationItem {
  id?: string;
  name: string | null;
  quantity: number;
  unit: string | null;
  goodsValueTotal: number;
  manualAllocatedTransportCost: number;
  manualAllocationInvalid: boolean;
  dimensionSource: ConversionSource;
  // Packaging (logistics) snapshot for BY_LDM's package-based footprint.
  piecesPerPackage: number | string | null | undefined;
  packageLengthMm: number | null;
  packageWidthMm: number | null;
  packageStackLayers: number | string | null | undefined;
  // F2 (manual LDM override): a positive value wins over packaging (see
  // resolveItemLdm). Normalized like a dimension — finite AND > 0, else null.
  manualLdm: number | null;
}

function toNumber(value: unknown): number {
  if (value === null || value === undefined) {
    return 0;
  }

  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

// Exported so sharedTransportFormUtils' goods-value prefill rounds ties (1.005 ->
// 1.01, 2.675 -> 2.68) identically to the allocation math, instead of the old
// toFixed(2) which truncated them (1.005 -> 1.00).
export function roundMoney(value: unknown): number {
  const numeric = toNumber(value);
  // Exponent-shift keeps decimal ties like 1.005 or 10.075 exact, where a
  // plain *100 multiply would land on x.4999... and round down.
  const rounded = Number(`${Math.round(Number(`${numeric}e2`))}e-2`);
  const result = Number.isFinite(rounded)
    ? rounded
    : Math.round((numeric + Number.EPSILON) * 100) / 100;
  return result === 0 ? 0 : result;
}

function normalizeMethod(method: TransportCostAllocationInput['method']): AllocationMethod {
  const normalized = String(method || DEFAULT_ALLOCATION_METHOD).toUpperCase();
  return (TRANSPORT_COST_ALLOCATION_METHODS as readonly string[]).includes(normalized)
    ? (normalized as AllocationMethod)
    : DEFAULT_ALLOCATION_METHOD;
}

function toDimensionMm(value: TransportCostAllocationInputItem[keyof TransportCostAllocationInputItem]): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
}

function normalizeItem(item: TransportCostAllocationInputItem = {}): NormalizedAllocationItem {
  const preferredName = item.productName || item.name || null;
  // A negative manual allocation violates the rulebook invariant "allocations
  // are never negative". Defense-in-depth at the util level (the service/API
  // validates >= 0 earlier): a negative value is nulled to 0 (item counts as
  // un-allocated) and flagged so the caller can raise NEGATIVE_MANUAL_ALLOCATION.
  const rawManual = roundMoney(item.manualAllocatedTransportCost);
  const manualAllocationInvalid = rawManual < 0;
  return {
    id: item.id,
    name: preferredName != null && String(preferredName).trim() !== '' ? String(preferredName) : null,
    quantity: toNumber(item.quantity),
    unit: item.unit || null,
    goodsValueTotal: roundMoney(item.goodsValueTotal),
    manualAllocatedTransportCost: manualAllocationInvalid ? 0 : rawManual,
    manualAllocationInvalid,
    // Real dimensions + real commercial unit — passed through as-is to
    // calculateAreaM2 / calculateVolumeM3, exactly like the backend twin's
    // calculateAllocationMetric(item, method) call on the raw normalized item.
    dimensionSource: {
      width: toDimensionMm(item.width),
      length: toDimensionMm(item.length),
      height: toDimensionMm(item.height),
      thickness: toDimensionMm(item.thickness),
      unit: item.unit || null,
    },
    piecesPerPackage: item.piecesPerPackage,
    packageLengthMm: toDimensionMm(item.packageLengthMm ?? item.packageLength),
    packageWidthMm: toDimensionMm(item.packageWidthMm ?? item.packageWidth),
    packageStackLayers: item.packageStackLayers,
    // F2 (manual LDM override): a positive value wins over packaging (see
    // resolveItemLdm). >0 mirrors toDimensionMm's "finite AND strictly
    // positive, else treated as absent" contract — a 0 or negative manualLdm
    // falls back to packaging, it is not a valid override.
    manualLdm: toDimensionMm(item.manualLdm),
  };
}

// Split transportCost across sourceValues by their non-negative weights.
//
// Cumulative-rounding (a form of the largest-remainder / Hamilton method):
// each allocation is round(cumulativeCost_i) - round(cumulativeCost_{i-1})
// over the RAW cumulative sums. Because the raw cumulative is monotonically
// non-decreasing when every weight is non-negative, every per-item difference
// is >= 0, and the differences telescope so the sum is EXACTLY
// round(cumulativeCost_last) = round(transportCost). This removes the old
// "remainder dumped on the last share" behaviour, which could go negative for
// tiny costs (e.g. 0.05 over 10 equal items -> [0.01 x9, -0.04]). Mirrors the
// backend twin's allocateProportionally exactly.
export function allocateProportionally(transportCost: number, sourceValues: number[]): number[] {
  const roundedTransportCost = roundMoney(transportCost);
  const weights = sourceValues.map((value) => Math.max(toNumber(value), 0));
  const total = weights.reduce((sum, value) => sum + value, 0);

  if (total <= 0) {
    return sourceValues.map(() => 0);
  }

  let cumulativeWeight = 0;
  let previousRoundedCost = 0;

  return weights.map((weight) => {
    if (weight <= 0) {
      return 0;
    }

    cumulativeWeight += weight;
    const cumulativeCost = roundMoney((cumulativeWeight / total) * roundedTransportCost);
    const allocation = roundMoney(cumulativeCost - previousRoundedCost);
    previousRoundedCost = cumulativeCost;
    return allocation;
  });
}

// ---------------------------------------------------------------------------
// Package-based LDM (loading metres) — the market-standard, position-based
// formula (docs/decisions/2026-07-06-ldm-package-based.md). LDM is the floor
// footprint that the PACKAGES/pallets occupy on a standard trailer bed
// (STANDARD_TRAILER_WIDTH_METERS wide), NOT the summed board area / 2.4 the old
// superseded floor-area formula produced (which yielded absurd values like 520
// LDM for 400 boards). Missing packaging data yields a null LDM + a
// MISSING_PACKAGING code so the UI shows "—" instead of a silent wrong number.
//
// Returns { ldm: number|null, code: 'MISSING_PACKAGING' | 'PACKAGE_EXCEEDS_TRAILER_WIDTH' | null }.
// Kept byte-identical to the backend mirror's calculatePackagingLdm.
// ---------------------------------------------------------------------------
export interface PackagingLdmInput {
  quantity?: number | string | null;
  unit?: string | null;
  width?: number | string | null;
  length?: number | string | null;
  height?: number | string | null;
  thickness?: number | string | null;
  piecesPerPackage?: number | string | null;
  packageLengthMm?: number | string | null;
  packageWidthMm?: number | string | null;
  packageStackLayers?: number | string | null;
}

export interface PackagingLdmResult {
  ldm: number | null;
  code: 'MISSING_PACKAGING' | 'PACKAGE_EXCEEDS_TRAILER_WIDTH' | null;
}

export function calculatePackagingLdm(input: PackagingLdmInput = {}): PackagingLdmResult {
  const quantity = toNumber(input.quantity);
  const unit = normalizeQuantityUnit(input.unit);
  const widthMm = toDimensionMm(input.width);
  const lengthMm = toDimensionMm(input.length);
  const heightMm = toDimensionMm(input.height);
  const thicknessMm = toDimensionMm(input.thickness);

  // 1. Resolve piece count from the commercial quantity + unit.
  let pieces: number;
  if (unit === 'm2') {
    const perPieceM2 = widthMm && lengthMm ? (widthMm / 1000) * (lengthMm / 1000) : null;
    if (!perPieceM2 || perPieceM2 <= 0) {
      return { ldm: null, code: 'MISSING_PACKAGING' };
    }
    pieces = quantity / perPieceM2;
  } else if (unit === 'm3') {
    const heightForVolumeMm = heightMm || thicknessMm;
    const perPieceM3 = widthMm && lengthMm && heightForVolumeMm
      ? (widthMm / 1000) * (lengthMm / 1000) * (heightForVolumeMm / 1000)
      : null;
    if (!perPieceM3 || perPieceM3 <= 0) {
      return { ldm: null, code: 'MISSING_PACKAGING' };
    }
    pieces = quantity / perPieceM3;
  } else {
    pieces = quantity;
  }

  if (!(pieces > 0)) {
    return { ldm: null, code: 'MISSING_PACKAGING' };
  }

  // 2. piecesPerPackage is required — no packages, no footprint.
  const piecesPerPackage = toNumber(input.piecesPerPackage);
  if (!(piecesPerPackage > 0)) {
    return { ldm: null, code: 'MISSING_PACKAGING' };
  }

  // 3. A partial package still occupies a full footprint.
  const packages = Math.ceil(pieces / piecesPerPackage);

  // 4. Package footprint (m). Falls back to the line dimensions when a package
  //    override is absent.
  const pkgWidthMm = toDimensionMm(input.packageWidthMm) || widthMm;
  const pkgLengthMm = toDimensionMm(input.packageLengthMm) || lengthMm;
  if (!pkgWidthMm || !pkgLengthMm) {
    return { ldm: null, code: 'MISSING_PACKAGING' };
  }
  // 5. Stack layers (≥1).
  const rawLayers = toNumber(input.packageStackLayers);
  const layers = rawLayers >= 1 ? rawLayers : 1;

  // 6. Orientation-aware positions. For each orientation the package footprint
  //    (a=along the trailer, b=across) is feasible iff b fits the trailer width;
  //    ldm = packages × a / rowsAcross / layers. Take the smallest feasible ldm.
  //    Row count is computed in MILLIMETRES: metre-space division breaks under
  //    IEEE 754 (2.4 / 0.8 === 2.9999999999999996 → floor 2 instead of 3 rows).
  const trailerWidthMm = Math.round(STANDARD_TRAILER_WIDTH_METERS * 1000);
  const orientations = [
    { alongMm: pkgLengthMm, acrossMm: pkgWidthMm },
    { alongMm: pkgWidthMm, acrossMm: pkgLengthMm },
  ];

  let bestLdm: number | null = null;
  orientations.forEach((orientation) => {
    if (orientation.acrossMm > trailerWidthMm) {
      return;
    }
    const rowsAcross = Math.max(1, Math.floor(trailerWidthMm / orientation.acrossMm));
    // All arithmetic stays in mm; convert to metres LAST so integer-exact
    // divisions (1200/3 = 400) survive instead of 1.2/3 = 0.3999999999999999….
    const ldm = (packages * orientation.alongMm) / rowsAcross / layers / 1000;
    if (bestLdm === null || ldm < bestLdm) {
      bestLdm = ldm;
    }
  });

  // 7. Neither orientation fits the trailer width: one package per row, warn.
  if (bestLdm === null) {
    const ldm = (packages * Math.min(pkgLengthMm, pkgWidthMm)) / layers / 1000;
    return { ldm, code: 'PACKAGE_EXCEEDS_TRAILER_WIDTH' };
  }

  return { ldm: bestLdm, code: null };
}

// F2 (manual LDM override): resolve the EFFECTIVE per-line LDM, preferring a
// positive operator-entered manualLdm over the packaging-based calculation.
// Single home for this precedence, byte-identical to the backend twin's
// resolveItemLdm, so calculateAllocationMetric's BY_LDM branch and the display
// metrics (sharedTransportFormUtils) can never disagree about which value
// "the" LDM for a line actually is. `input.manualLdm` must already be
// normalized (finite AND > 0, else absent) — this function does not
// re-validate it.
export interface ResolveItemLdmInput extends PackagingLdmInput {
  manualLdm?: number | string | null;
}

export interface ResolveItemLdmResult extends PackagingLdmResult {
  isManual: boolean;
}

export function resolveItemLdm(input: ResolveItemLdmInput = {}): ResolveItemLdmResult {
  const manualLdm = toDimensionMm(input.manualLdm);
  if (manualLdm) {
    return { ldm: manualLdm, code: null, isManual: true };
  }
  const packagingResult = calculatePackagingLdm(input);
  return { ldm: packagingResult.ldm, code: packagingResult.code, isManual: false };
}

// Per-line metric for a dimension-based method (m², m³, or LDM). Mirrors the
// backend twin's calculateAllocationMetric 1:1, reusing the shared
// dimensionConversion helpers so geometry/unit handling is never re-derived
// here. Returns null when the line lacks the dimensions the method needs, so
// the caller can raise MISSING_DIMENSIONS instead of silently allocating zero.
function calculateAllocationMetric(item: NormalizedAllocationItem, method: AllocationMethod): number | null {
  if (method === 'BY_SQM') {
    return calculateAreaM2(item.dimensionSource, item.quantity);
  }

  if (method === 'BY_CBM') {
    return calculateVolumeM3(item.dimensionSource, item.quantity);
  }

  if (method === 'BY_LDM') {
    // Package-based LDM (or a manual override — see resolveItemLdm): items
    // without EITHER return null so the caller raises MISSING_PACKAGING
    // (surfaced as an aggregated warning like MISSING_DIMENSIONS).
    return resolveItemLdm({
      quantity: item.quantity,
      unit: item.dimensionSource.unit,
      width: item.dimensionSource.width,
      length: item.dimensionSource.length,
      height: item.dimensionSource.height,
      thickness: item.dimensionSource.thickness,
      piecesPerPackage: item.piecesPerPackage,
      packageLengthMm: item.packageLengthMm,
      packageWidthMm: item.packageWidthMm,
      packageStackLayers: item.packageStackLayers,
      manualLdm: item.manualLdm,
    }).ldm;
  }

  return item.quantity;
}

function buildMissingDimensionsWarning(items: NormalizedAllocationItem[]): TransportCostAllocationWarning {
  return {
    code: 'MISSING_DIMENSIONS',
    items: items.map((item) => ({ itemId: item.id, name: item.name })),
  };
}

// Aggregated warning ({code, items:[{itemId,name}]}) for a metric method whose
// per-line basis could not be derived. BY_LDM is package-based, so its missing
// state is MISSING_PACKAGING (fill in packaging data); BY_SQM/BY_CBM keep
// MISSING_DIMENSIONS. Same shape either way so the UI renders both identically.
// Mirrors the backend twin's buildMissingMetricWarning.
function buildMissingMetricWarning(
  items: NormalizedAllocationItem[],
  method: AllocationMethod,
): TransportCostAllocationWarning {
  if (method === 'BY_LDM') {
    return {
      code: 'MISSING_PACKAGING',
      items: items.map((item) => ({ itemId: item.id, name: item.name })),
    };
  }
  return buildMissingDimensionsWarning(items);
}

// Aggregated warning for MANUAL lines whose manual allocation was negative and
// therefore nulled to 0 (rulebook: allocations are never negative). Same shape
// as buildMissingDimensionsWarning so the UI renders both identically. Mirrors
// the backend twin's buildNegativeManualAllocationWarning.
function buildNegativeManualAllocationWarning(items: NormalizedAllocationItem[]): TransportCostAllocationWarning {
  return {
    code: 'NEGATIVE_MANUAL_ALLOCATION',
    items: items.map((item) => ({ itemId: item.id, name: item.name })),
  };
}

// Methods whose per-line basis comes from product dimensions (mm) rather than
// quantity/manual. Used to decide when to compute metrics and to warn on
// missing dimensions or a zero total metric. Mirrors the backend twin's
// isMetricAllocationMethod.
function isMetricAllocationMethod(method: AllocationMethod): boolean {
  return (['BY_SQM', 'BY_CBM', 'BY_LDM'] as const).includes(method as 'BY_SQM' | 'BY_CBM' | 'BY_LDM');
}

// OSCAR (PDF-contract wave, addition 1): mirrors the backend twin's
// buildPerMetricUnitCosts exactly — SAME area/volume/LDM single homes
// (calculateAreaM2/calculateVolumeM3/resolveItemLdm) the BY_SQM/BY_CBM/BY_LDM
// allocation metric itself uses, so "cost per m²" can never disagree with
// the m² the allocation actually split by. null (not 0) when the metric
// cannot be computed for this line — the UI renders "—", never a fabricated
// number.
function buildPerMetricUnitCosts(
  item: NormalizedAllocationItem,
  roundedAllocatedTransportCost: number,
): Pick<TransportCostAllocationItemResult, 'transportCostPerAreaSqm' | 'transportCostPerVolumeM3' | 'transportCostPerLdm'> {
  const areaSqm = calculateAreaM2(item.dimensionSource, item.quantity);
  const volumeM3 = calculateVolumeM3(item.dimensionSource, item.quantity);
  // resolveItemLdm needs the flat shape (width/length/... at the top level,
  // not nested under dimensionSource) — same call shape as the BY_LDM branch
  // of calculateAllocationMetric above, so this can never disagree with the
  // LDM the allocation itself would have split by.
  const { ldm } = resolveItemLdm({
    quantity: item.quantity,
    unit: item.dimensionSource.unit,
    width: item.dimensionSource.width,
    length: item.dimensionSource.length,
    height: item.dimensionSource.height,
    thickness: item.dimensionSource.thickness,
    piecesPerPackage: item.piecesPerPackage,
    packageLengthMm: item.packageLengthMm,
    packageWidthMm: item.packageWidthMm,
    packageStackLayers: item.packageStackLayers,
    manualLdm: item.manualLdm,
  });

  return {
    transportCostPerAreaSqm: areaSqm && areaSqm > 0 ? roundMoney(roundedAllocatedTransportCost / areaSqm) : null,
    transportCostPerVolumeM3: volumeM3 && volumeM3 > 0 ? roundMoney(roundedAllocatedTransportCost / volumeM3) : null,
    transportCostPerLdm: ldm && ldm > 0 ? roundMoney(roundedAllocatedTransportCost / ldm) : null,
  };
}

function buildItemOutput(
  item: NormalizedAllocationItem,
  allocatedTransportCost: number,
): TransportCostAllocationItemResult {
  const roundedAllocatedTransportCost = roundMoney(allocatedTransportCost);
  const landedCostTotal = roundMoney(item.goodsValueTotal + roundedAllocatedTransportCost);
  const hasPositiveQuantity = item.quantity > 0;

  return {
    id: item.id,
    quantity: item.quantity,
    unit: item.unit,
    goodsValueTotal: item.goodsValueTotal,
    manualAllocatedTransportCost: item.manualAllocatedTransportCost,
    allocatedTransportCost: roundedAllocatedTransportCost,
    goodsCostPerUnit: hasPositiveQuantity ? roundMoney(item.goodsValueTotal / item.quantity) : null,
    transportCostPerUnit: hasPositiveQuantity
      ? roundMoney(roundedAllocatedTransportCost / item.quantity)
      : null,
    landedCostTotal,
    landedCostPerUnit: hasPositiveQuantity ? roundMoney(landedCostTotal / item.quantity) : null,
    ...buildPerMetricUnitCosts(item, roundedAllocatedTransportCost),
  };
}

export function calculateTransportCostAllocation({
  transportCost,
  method,
  items,
}: TransportCostAllocationInput = {}): TransportCostAllocationResult {
  const normalizedMethod = normalizeMethod(method);
  const normalizedItems = Array.isArray(items) ? items.map(normalizeItem) : [];
  const roundedTransportCost = roundMoney(transportCost);
  const warnings: TransportCostAllocationWarning[] = [];

  normalizedItems.forEach((item) => {
    if (item.quantity <= 0) {
      warnings.push({ code: 'NON_POSITIVE_QUANTITY', itemId: item.id });
    }
  });

  let allocations: number[] = normalizedItems.map(() => 0);

  if (normalizedMethod === 'MANUAL') {
    // Negatives were already nulled to 0 in normalizeItem; surface an aggregated
    // warning so the caller knows why those lines carry nothing. The nulling
    // also lets MANUAL_SUM_MISMATCH fire naturally when the remaining sum no
    // longer matches the transport cost.
    const negativeManualItems = normalizedItems.filter((item) => item.manualAllocationInvalid);
    if (negativeManualItems.length > 0) {
      warnings.push(buildNegativeManualAllocationWarning(negativeManualItems));
    }

    allocations = normalizedItems.map((item) => item.manualAllocatedTransportCost);
  } else {
    // Quantity <= 0 means "not loaded on this transport": those items never
    // receive an automatic allocation, under any method.
    const eligibleIndexes = normalizedItems.reduce<number[]>((indexes, item, index) => {
      if (item.quantity > 0) {
        indexes.push(index);
      }
      return indexes;
    }, []);

    if (eligibleIndexes.length === 0) {
      warnings.push({ code: 'ZERO_TOTAL_QUANTITY' });
    } else if (eligibleIndexes.length === 1) {
      // A single loaded position always gets 100% of the transport cost, but a
      // dimension method still flags missing dimensions (mirrors the backend so
      // the operator sees the same "fill in dimensions" cue in the single case).
      if (isMetricAllocationMethod(normalizedMethod)) {
        const metric = calculateAllocationMetric(normalizedItems[eligibleIndexes[0]], normalizedMethod);
        if (metric === null) {
          warnings.push(buildMissingMetricWarning([normalizedItems[eligibleIndexes[0]]], normalizedMethod));
        }
      }

      allocations[eligibleIndexes[0]] = roundedTransportCost;
    } else if (isMetricAllocationMethod(normalizedMethod)) {
      // BY_SQM / BY_CBM / BY_LDM: basis is the per-line metric derived from the
      // product dimensions (honouring the commercial unit shortcut per method).
      // Loaded lines without the dimensions the method needs are collected into
      // one aggregated MISSING_DIMENSIONS warning rather than silently
      // allocating zero.
      const missingDimensionItems: NormalizedAllocationItem[] = [];
      const sourceValues = normalizedItems.map((item) => {
        if (item.quantity <= 0) {
          return 0;
        }

        const metric = calculateAllocationMetric(item, normalizedMethod);
        if (metric === null) {
          missingDimensionItems.push(item);
          return 0;
        }

        return Math.max(toNumber(metric), 0);
      });
      const totalMetric = sourceValues.reduce((sum, value) => sum + value, 0);

      if (missingDimensionItems.length > 0) {
        warnings.push(buildMissingMetricWarning(missingDimensionItems, normalizedMethod));
      }

      if (totalMetric <= 0) {
        // Same no-allocation semantics as ZERO_TOTAL_QUANTITY: nothing carries a
        // usable metric, so no automatic split is possible.
        warnings.push({ code: 'ZERO_TOTAL_METRIC' });
      } else {
        allocations = allocateProportionally(roundedTransportCost, sourceValues);
      }
    } else {
      const nonEmptyUnits = new Set(
        eligibleIndexes
          .map((index) => {
            const unit = normalizedItems[index].unit;
            return unit === null || unit === undefined ? '' : String(unit).trim();
          })
          .filter(Boolean),
      );

      if (nonEmptyUnits.size > 1) {
        warnings.push({ code: 'MIXED_UNITS' });
      }

      allocations = allocateProportionally(
        roundedTransportCost,
        normalizedItems.map((item) => (item.quantity > 0 ? item.quantity : 0)),
      );
    }
  }

  const outputItems = normalizedItems.map((item, index) => buildItemOutput(item, allocations[index] || 0));
  const totalGoodsValue = roundMoney(outputItems.reduce((sum, item) => sum + item.goodsValueTotal, 0));
  const totalAllocatedTransportCost = roundMoney(
    outputItems.reduce((sum, item) => sum + item.allocatedTransportCost, 0),
  );
  const totalLandedCost = roundMoney(totalGoodsValue + totalAllocatedTransportCost);
  const allocationDifference = roundMoney(roundedTransportCost - totalAllocatedTransportCost);

  if (normalizedMethod === 'MANUAL' && Math.abs(allocationDifference) >= 0.01) {
    warnings.push({ code: 'MANUAL_SUM_MISMATCH' });
  }

  return {
    method: normalizedMethod,
    items: outputItems,
    summary: {
      totalGoodsValue,
      totalAllocatedTransportCost,
      totalLandedCost,
      allocationDifference,
    },
    warnings,
  };
}
